import { SeekApiPayload } from '@seek/indie-api-types';
import {
  Actions,
  Button,
  ButtonLink,
  Dropdown,
  HiddenVisually,
  Stack,
  TextField,
} from 'braid-design-system';
import {
  createContext,
  type FormEvent,
  type ReactNode,
  useContext,
  useEffect,
} from 'react';

import { useEnvironmentConfig } from '#src/hooks/environment.tsx';

import type {
  CreateNewWebhookMutationVariables,
  GetHirerScopedWebhookSubscriptionCountQuery,
  GetHirerScopedWebhookSubscriptionCountQueryVariables,
} from '#src/types/generated/graphql.ts';

import { DeliveryForm } from '../../../SubscriptionForm/DeliveryForm.tsx';
import { SigningForm } from '../../../SubscriptionForm/SigningForm.tsx';
import { HirerPicker, useHirerState } from '../HirerPicker/HirerPicker.tsx';

import { type FormData, FormProvider, useField } from './form.ts';
import { useRequiredField } from './useRequiredField.tsx';
import type { RenderParams } from '@seek/forms-ui';
import { useQuery } from '@apollo/client/react';
import { GET_HIRER_SCOPED_WEBHOOK_SUBSCRIPTION_COUNT } from '../../queries';
import { useGrants } from '#src/hooks/grants.tsx';

const eventTypeCodes = SeekApiPayload.EventType.alternatives.map(
  (alt) => alt.value,
);

interface FormProps {
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  loading: boolean;
}

const HIRER_SCOPED_DEFAULT_LIMIT = 20;

const useHirerScopedLimits = () => {
  const { seekAnzSchemeId } = useEnvironmentConfig();

  const grants = useGrants();

  const { data: hirerScopedSubscriptionCountQueryData } = useQuery<
    GetHirerScopedWebhookSubscriptionCountQuery,
    GetHirerScopedWebhookSubscriptionCountQueryVariables
  >(GET_HIRER_SCOPED_WEBHOOK_SUBSCRIPTION_COUNT, {
    variables: {
      schemeId: seekAnzSchemeId,
    },
  });

  const hirerScopedSubscriptionCount =
    hirerScopedSubscriptionCountQueryData?.webhookSubscriptions.totalCount ??
    null;

  if (grants === null || grants.unlimitedHirerScopedWebhooks) {
    return {
      message: null,
      disabled: false,
    };
  }

  if (hirerScopedSubscriptionCount === null) {
    return {
      message: `You can have up to ${HIRER_SCOPED_DEFAULT_LIMIT} hirer-scoped webhook subscriptions.`,
      disabled: false,
    };
  }

  if (hirerScopedSubscriptionCount < HIRER_SCOPED_DEFAULT_LIMIT) {
    return {
      message: `You have used ${
        hirerScopedSubscriptionCount
      } of your ${HIRER_SCOPED_DEFAULT_LIMIT} available hirer-scoped webhook subscriptions so far.`,
      disabled: false,
    };
  }

  return {
    message: `You have used ${hirerScopedSubscriptionCount} of your ${HIRER_SCOPED_DEFAULT_LIMIT} available hirer-scoped webhook subscriptions so far. Delete some before adding more.`,
    disabled: true,
  };
};

const Form = ({ onSubmit, loading }: FormProps) => {
  const eventTypeCode = useRequiredField('eventTypeCode');
  const schemeId = useRequiredField('schemeId');
  const hirerId = useRequiredField('hirerId', false);

  const hirerState = useHirerState();
  const hirerScopedLimits = useHirerScopedLimits();

  useEffect(() => {
    hirerId.onChange(hirerState.hirer?.id.value);
  }, [hirerId, hirerState.hirer?.id.value]);

  return (
    <form onSubmit={onSubmit}>
      <Stack space="large">
        <Dropdown
          placeholder="Select event type..."
          disabled={loading}
          description="Subscriptions are scoped to a specific event type. Use this field to nominate which event type this subscription receives."
          {...eventTypeCode}
        >
          {eventTypeCodes.map((code) => (
            <option key={code} value={code}>
              {code}
            </option>
          ))}
        </Dropdown>

        <DeliveryForm<FormData>
          disabled={loading}
          mode="create"
          useField={useField}
        />

        {eventTypeCode.value !== 'HirerRelationshipChanged' && (
          <HirerPicker
            id={hirerId.id}
            disabled={hirerScopedLimits.disabled}
            extraDescription={hirerScopedLimits.message}
            extraDescriptionTone={
              hirerScopedLimits.disabled ? 'caution' : 'neutral'
            }
            {...hirerState}
          />
        )}

        <SigningForm disabled={loading} mode="create" useField={useField} />

        {/* There's only one valid scheme ID at the moment, so no need to show the field */}
        <HiddenVisually>
          <TextField {...schemeId} />
        </HiddenVisually>

        <Actions>
          <Button loading={loading} tone="brandAccent" type="submit">
            {loading ? 'Creating' : 'Create'}
          </Button>
          <ButtonLink href="/manage/webhooks" variant="transparent">
            Cancel
          </ButtonLink>
        </Actions>
      </Stack>
    </form>
  );
};

interface NewWebhookFormProps {
  onSubmit: (details: CreateNewWebhookMutationVariables) => void;
  loading: boolean;
}

const initialValues: Omit<FormData, 'schemeId'> = {
  // schemeId is dynamic and pulled from useEnvironmentConfig hook
  eventTypeCode: '',
  signingAlgorithmCode: 'SeekHmacSha512',
  url: '',
  hirerId: undefined,
  maxEventsPerAttempt: undefined,
  secret: '',
};

const parseSubmittedData = (
  inputValues: FormData,
): CreateNewWebhookMutationVariables => ({
  ...inputValues,
  secret: inputValues.secret,
  maxEventsPerAttempt: inputValues.maxEventsPerAttempt
    ? parseInt(inputValues.maxEventsPerAttempt, 10)
    : undefined,
});

const NewWebhookFormContext = createContext<{
  handleSubmit: RenderParams<FormData>['handleSubmit'];
} | null>(null);

export const NewWebhookFormProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { seekAnzSchemeId } = useEnvironmentConfig();

  return (
    <FormProvider
      initialValues={{ ...initialValues, schemeId: seekAnzSchemeId }}
    >
      {({ handleSubmit }) => (
        <NewWebhookFormContext.Provider value={{ handleSubmit }}>
          {children}
        </NewWebhookFormContext.Provider>
      )}
    </FormProvider>
  );
};

export const NewWebhookForm = ({ onSubmit, loading }: NewWebhookFormProps) => {
  const context = useContext(NewWebhookFormContext);

  if (!context) {
    throw new Error(
      'NewWebhookForm must be used within a NewWebhookFormProvider',
    );
  }

  const { handleSubmit } = context;

  return (
    <Form
      loading={loading}
      onSubmit={handleSubmit((data) => {
        const parsedData = parseSubmittedData(data);
        onSubmit({
          ...parsedData,
          hirerId:
            parsedData.eventTypeCode === 'HirerRelationshipChanged'
              ? null
              : parsedData.hirerId,
        });
      })}
    />
  );
};
